import React, { useEffect, useMemo, useState } from "react";
import {
    Shield, Network, Wifi, Router, Server, HardDrive, Battery, Printer,
    Laptop, Phone, Camera, Cpu, Box, Pencil, Trash2, RotateCcw, Plus, Download, Loader2, Search,
} from "lucide-react";
import { toast } from "sonner";
import { getJSON, delJSON, postJSON } from "../../lib/api";
import { MagneticButton } from "../MagneticButton";
import { HardwareForm } from "./HardwareForm";

/** Map asset.category → Lucide icon. Single source of truth for the tab. */
const CATEGORY_ICON = {
    firewall: Shield,
    switch: Network,
    access_point: Wifi,
    router: Router,
    server: Server,
    nas: HardDrive,
    ups: Battery,
    printer: Printer,
    endpoint: Laptop,
    phone: Phone,
    camera: Camera,
    iot: Cpu,
    other: Box,
};

const STATUS_TONES = {
    active: "border-accent-mint/30 text-accent-mint bg-accent-mint/[0.08]",
    spare: "border-white/[0.1] text-text-muted",
    decommissioned: "border-accent-amber/30 text-accent-amber bg-accent-amber/[0.06]",
};

/** Trigger a browser download of a backend CSV stream. We can't use raw axios
 *  here because of the file-download Content-Disposition; an anchor element
 *  is the cleanest, most reliable cross-browser approach. */
async function downloadCsv(client) {
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/ipam/clients/${client.id}/export.csv?include=subnets,hardware`;
    try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const ymd = new Date().toISOString().slice(0, 10);
        a.download = `${client.slug}-export-${ymd}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("CSV downloaded");
    } catch (e) {
        toast.error(e.message || "Download failed");
    }
}

export const HardwareTab = ({ client, onChanged }) => {
    const [items, setItems] = useState([]);
    const [subnets, setSubnets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null, "new", or asset obj
    const [statusFilter, setStatusFilter] = useState("active");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const qp = new URLSearchParams();
            if (statusFilter && statusFilter !== "all") qp.set("status", statusFilter);
            if (categoryFilter) qp.set("category", categoryFilter);
            if (search.trim()) qp.set("q", search.trim());
            const [hw, subs] = await Promise.all([
                getJSON(`/ipam/clients/${client.id}/hardware?${qp.toString()}`),
                getJSON(`/ipam/clients/${client.id}/subnets`),
            ]);
            setItems(hw.items || []);
            setSubnets(subs.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load hardware");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter]);
    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    const decommission = async (a) => {
        if (!window.confirm(`Decommission ${a.vendor} ${a.model} (${a.hostname || "no hostname"})? It will be hidden from the active list.`)) return;
        try {
            await delJSON(`/ipam/hardware/${a.id}`);
            toast.success("Asset decommissioned");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e.message || "Failed to decommission");
        }
    };

    const restore = async (a) => {
        try {
            await postJSON(`/ipam/hardware/${a.id}/restore`, {});
            toast.success("Asset restored");
            await load();
        } catch (e) {
            toast.error(e.message || "Failed to restore");
        }
    };

    const subnetCidrById = useMemo(() => {
        const map = new Map();
        subnets.forEach((s) => map.set(s.id, s.cidr));
        return map;
    }, [subnets]);

    return (
        <div data-testid="client-hardware-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[13px] text-text-muted">
                    {loading ? "…" : `${items.length} ${items.length === 1 ? "asset" : "assets"}`}
                    {categoryFilter && <span> · category: <span className="text-text-primary">{categoryFilter}</span></span>}
                </p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => downloadCsv(client)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] bg-elevated/40 text-[12.5px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30 transition-colors"
                        data-testid="hw-export-csv-btn"
                        title="Download a CSV with subnets + hardware for this client"
                    >
                        <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                    <MagneticButton
                        type="button"
                        onClick={() => setEditing("new")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan"
                        ariaLabel="Add asset"
                        data-testid="hw-add-btn"
                    >
                        <Plus className="w-3.5 h-3.5" /> Add Asset
                    </MagneticButton>
                </div>
            </div>

            {/* Filters */}
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="hw-filters">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-dim" />
                    <input
                        type="text"
                        placeholder="Search hostname, IP, serial, model…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[12.5px] placeholder:text-text-dim focus:outline-none focus:border-accent-cyan/40"
                        data-testid="hw-search"
                    />
                </div>
                <div className="inline-flex rounded-md border border-white/[0.06] bg-surface/60 p-0.5">
                    {["active", "spare", "decommissioned"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatusFilter(s)}
                            className={`px-2.5 py-1 text-[11.5px] font-medium rounded-[5px] transition-colors ${
                                statusFilter === s ? "bg-elevated text-text-primary" : "text-text-muted hover:text-text-primary"
                            }`}
                            data-testid={`hw-status-${s}`}
                        >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
                {Object.keys(CATEGORY_ICON).map((c) => {
                    const active = categoryFilter === c;
                    return (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setCategoryFilter(active ? null : c)}
                            className={`px-2 py-0.5 rounded text-[10.5px] font-medium border transition-colors ${
                                active
                                    ? "bg-accent-cyan/[0.12] border-accent-cyan/40 text-accent-cyan"
                                    : "border-white/[0.06] text-text-muted hover:border-accent-cyan/30"
                            }`}
                            data-testid={`hw-filter-category-${c}`}
                        >
                            {c}
                        </button>
                    );
                })}
            </div>

            {/* Body */}
            {loading ? (
                <div className="mt-10 flex items-center justify-center text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading hardware…
                </div>
            ) : items.length === 0 ? (
                <div className="mt-10 text-center" data-testid="hw-empty">
                    <p className="text-text-muted text-[13px]">
                        {statusFilter === "decommissioned"
                            ? "No decommissioned assets."
                            : "No hardware tracked for this client yet."}
                    </p>
                    {statusFilter === "active" && (
                        <MagneticButton
                            type="button"
                            onClick={() => setEditing("new")}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan"
                            ariaLabel="Add first asset"
                            data-testid="hw-empty-cta"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add first asset
                        </MagneticButton>
                    )}
                </div>
            ) : (
                <div className="mt-4 grid gap-2">
                    {items.map((a) => {
                        const Icon = CATEGORY_ICON[a.category] || Box;
                        const subnetCidr = a.subnet_id ? subnetCidrById.get(a.subnet_id) : null;
                        return (
                            <div
                                key={a.id}
                                className="rounded-lg border border-white/[0.06] bg-surface/40 hover:border-accent-cyan/30 transition-colors p-3 flex items-center gap-3 flex-wrap"
                                data-testid={`hw-row-${a.id}`}
                            >
                                <Icon className="w-4 h-4 text-accent-cyan shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[13px] font-medium text-text-primary truncate">
                                            {a.vendor} <span className="text-text-muted">{a.model}</span>
                                        </span>
                                        <span className={`text-[10px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded border ${STATUS_TONES[a.status] || ""}`}>
                                            {a.status}
                                        </span>
                                        {a.tags?.map((t) => (
                                            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/[0.08] text-text-dim">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-1 text-[11.5px] text-text-dim flex flex-wrap gap-3 font-mono">
                                        {a.hostname && <span>{a.hostname}</span>}
                                        {a.ip_address && <span className="text-text-muted">{a.ip_address}</span>}
                                        {a.mac_address && <span>{a.mac_address}</span>}
                                        {subnetCidr && <span>↳ {subnetCidr}</span>}
                                        {a.firmware_version && <span>fw {a.firmware_version}</span>}
                                        {a.location && <span className="text-text-muted">{a.location}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {a.status === "decommissioned" ? (
                                        <button
                                            type="button"
                                            onClick={() => restore(a)}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-accent-mint/30 text-[11px] text-accent-mint hover:bg-accent-mint/[0.08]"
                                            data-testid={`hw-restore-${a.id}`}
                                            title="Restore to active"
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => setEditing(a)}
                                                className="inline-flex items-center px-2 py-1 rounded border border-white/[0.06] text-[11px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30"
                                                data-testid={`hw-edit-${a.id}`}
                                            >
                                                <Pencil className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => decommission(a)}
                                                className="inline-flex items-center px-2 py-1 rounded border border-white/[0.06] text-[11px] text-text-muted hover:text-accent-amber hover:border-accent-amber/30"
                                                data-testid={`hw-decommission-${a.id}`}
                                                title="Decommission (soft delete)"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing !== null && (
                <HardwareForm
                    client={client}
                    subnets={subnets}
                    asset={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); onChanged?.(); }}
                />
            )}
        </div>
    );
};

export default HardwareTab;
